/**
 * Centralized permission utility.
 *
 * Rules:
 * - Uses react-native-permissions exclusively (no PermissionsAndroid).
 * - androidApi is computed once at module load; Platform.Version is static.
 * - Storage permissions are API-level fenced:
 *     WRITE_EXTERNAL_STORAGE: maxSdkVersion="29" → request only on API < 30.
 *     READ_EXTERNAL_STORAGE:  maxSdkVersion="32" → request only on API < 33.
 * - All public helpers return { granted, statuses } so callers can act on
 *   BLOCKED state without branching on raw status strings themselves.
 */
import { Platform } from "react-native";
import {
  check,
  request,
  requestMultiple,
  PERMISSIONS,
  RESULTS,
} from "react-native-permissions";

const androidApi =
  Platform.OS === "android" ? (Platform.Version) : 0;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function allGranted(statuses) {
  return Object.values(statuses).every(
    (s) => s === RESULTS.GRANTED || s === RESULTS.LIMITED,
  );
}

async function _requestMultiple(permissions) {
  if (permissions.length === 0) return { granted: true, statuses: {} };
  const statuses = await requestMultiple(permissions);
  return { granted: allGranted(statuses), statuses };
}

/** Storage permissions that apply at this API level. */
function storagePermissions() {
  if (Platform.OS !== "android") return [];
  if (androidApi >= 33) return [];
  const perms = [PERMISSIONS.ANDROID.READ_EXTERNAL_STORAGE];
  if (androidApi < 30) perms.push(PERMISSIONS.ANDROID.WRITE_EXTERNAL_STORAGE);
  return perms;
}

// ---------------------------------------------------------------------------
// Camera
// ---------------------------------------------------------------------------

export const requestCamera = async () => {
  if (Platform.OS === "ios") {
    const status = await request(PERMISSIONS.IOS.CAMERA);
    return {
      granted: status === RESULTS.GRANTED || status === RESULTS.LIMITED,
      statuses: { [PERMISSIONS.IOS.CAMERA]: status },
    };
  }
  const status = await request(PERMISSIONS.ANDROID.CAMERA);
  return {
    granted: status === RESULTS.GRANTED,
    statuses: { [PERMISSIONS.ANDROID.CAMERA]: status },
  };
};

// Legacy alias — callers that expect a boolean still work.
export const cameraPermission = async () => {
  const { granted } = await requestCamera();
  return granted;
};

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

/**
 * Storage-only permissions for file sharing (WhatsApp etc.).
 * API 33+: no-op — READ_MEDIA_* not needed for our use case.
 */
export const requestFileSharePermissions = async () =>
  _requestMultiple(storagePermissions());

/**
 * Legacy boolean helper kept for handleFSPermission.js callers.
 * API 30+: write is automatically true; API 33+: both are true.
 */
export const filePermission = async () => {
  const { granted } = await requestFileSharePermissions();
  return granted;
};

// ---------------------------------------------------------------------------
// Location
// ---------------------------------------------------------------------------

export const requestLocation = async () => {
  if (Platform.OS === "ios") {
    const status = await request(PERMISSIONS.IOS.LOCATION_WHEN_IN_USE);
    return {
      granted: status === RESULTS.GRANTED || status === RESULTS.LIMITED,
      statuses: { [PERMISSIONS.IOS.LOCATION_WHEN_IN_USE]: status },
    };
  }
  const status = await request(PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION);
  return {
    granted: status === RESULTS.GRANTED,
    statuses: { [PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION]: status },
  };
};

export const checkLocation = async () => {
  if (Platform.OS === "ios") {
    const status = await check(PERMISSIONS.IOS.LOCATION_WHEN_IN_USE);
    return {
      granted: status === RESULTS.GRANTED || status === RESULTS.LIMITED,
      statuses: { [PERMISSIONS.IOS.LOCATION_WHEN_IN_USE]: status },
    };
  }
  const status = await check(PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION);
  return {
    granted: status === RESULTS.GRANTED,
    statuses: { [PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION]: status },
  };
};

/** Legacy — returns raw PermissionStatus string for backward compat. */
export const fineLocationPermission = async () => {
  if (Platform.OS === "ios") {
    return request(PERMISSIONS.IOS.LOCATION_WHEN_IN_USE);
  }
  return request(PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION);
};

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

/**
 * Only a runtime permission on Android API 33+.
 * On lower APIs the OS grants it implicitly, so we return granted immediately.
 */
export const requestNotifications = async () => {
  if (Platform.OS === "ios") {
    return { granted: true, statuses: {} };
  }
  if (androidApi < 33) {
    return { granted: true, statuses: {} };
  }
  const POST_NOTIFICATIONS = PERMISSIONS.ANDROID.POST_NOTIFICATIONS;
  const status = await request(POST_NOTIFICATIONS);
  return {
    granted: status === RESULTS.GRANTED,
    statuses: { [POST_NOTIFICATIONS]: status },
  };
};

// ---------------------------------------------------------------------------
// Image picker combo
// ---------------------------------------------------------------------------

/**
 * Camera + storage combo used by image pickers.
 * API 33+: CAMERA only (image-picker uses the system photo picker).
 * API 30–32: CAMERA + READ_EXTERNAL_STORAGE.
 * API < 30: CAMERA + READ + WRITE_EXTERNAL_STORAGE.
 */
export const requestImagePickerPermissions = async () => {
  if (Platform.OS !== "android") {
    return { granted: true, statuses: {} };
  }
  const perms = [PERMISSIONS.ANDROID.CAMERA, ...storagePermissions()];
  return _requestMultiple(perms);
};

export default cameraPermission;
